import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  algorithmsService,
  type Difficulty,
  type ProblemStatus,
  type AlgoStats,
} from '../services/algorithms.service';
import { useAuth } from '../contexts/AuthContext';

const TOPICS = [
  'Arrays', 'Hash Map', 'Stack', 'String', 'Sliding Window',
  'Binary Search', 'Dynamic Programming', 'Graphs', 'Trees', 'Linked List',
];

function DifficultyBadge({ difficulty }: { difficulty: Difficulty }) {
  const styles: Record<Difficulty, string> = {
    EASY: 'bg-emerald-500/10 text-emerald-400',
    MEDIUM: 'bg-tertiary/10 text-tertiary',
    HARD: 'bg-error/10 text-error',
  };
  return (
    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded ${styles[difficulty]}`}>
      {difficulty}
    </span>
  );
}

function StatusIcon({ status }: { status: ProblemStatus }) {
  if (status === 'SOLVED')
    return <span className="material-symbols-outlined text-emerald-400 text-base">check_circle</span>;
  if (status === 'ATTEMPTED')
    return <span className="material-symbols-outlined text-tertiary text-base">pending</span>;
  return <span className="material-symbols-outlined text-outline text-base">radio_button_unchecked</span>;
}

function StatsBar({ stats }: { stats: AlgoStats }) {
  const pct = stats.total > 0 ? Math.round((stats.solved / stats.total) * 100) : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {[
        { label: 'Solved', value: `${stats.solved}/${stats.total}`, sub: `${pct}%` },
        { label: 'Easy', value: stats.byDifficulty.EASY, color: 'text-emerald-400' },
        { label: 'Medium', value: stats.byDifficulty.MEDIUM, color: 'text-tertiary' },
        { label: 'Hard', value: stats.byDifficulty.HARD, color: 'text-error' },
      ].map((item) => (
        <div key={item.label} className="bg-surface-container-low rounded-xl p-4">
          <p className={`text-2xl font-bold ${item.color ?? 'text-on-surface'}`}>{item.value}</p>
          {'sub' in item && <p className="text-xs text-primary font-bold">{item.sub}</p>}
          <p className="text-xs text-on-surface-variant mt-0.5">{item.label}</p>
        </div>
      ))}
    </div>
  );
}

export default function AlgorithmList() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [difficulty, setDifficulty] = useState<Difficulty | ''>('');
  const [topic, setTopic] = useState('');
  const [page, setPage] = useState(1);

  const enabled = !authLoading && !!user;

  const { data, isLoading } = useQuery({
    queryKey: ['algo-problems', difficulty, topic, page],
    queryFn: () =>
      algorithmsService.listProblems({
        difficulty: difficulty || undefined,
        topic: topic || undefined,
        page,
      }),
    enabled,
  });

  const { data: stats } = useQuery({
    queryKey: ['algo-stats'],
    queryFn: () => algorithmsService.getStats(),
    enabled,
  });

  const handleDifficulty = (d: Difficulty | '') => {
    setDifficulty(d);
    setPage(1);
  };

  const handleTopic = (t: string) => {
    setTopic(t === topic ? '' : t);
    setPage(1);
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-2">
          Algorithm Practice
        </p>
        <h1 className="text-3xl font-bold text-on-surface">Problem Set</h1>
        <p className="text-on-surface-variant mt-2">
          Practice data structures and algorithms for technical interviews.
        </p>
      </div>

      {/* Stats */}
      {stats && <StatsBar stats={stats} />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Difficulty filter */}
        <div className="flex items-center gap-1 bg-surface-container-low rounded-lg p-1">
          {(['', 'EASY', 'MEDIUM', 'HARD'] as const).map((d) => (
            <button
              key={d}
              onClick={() => handleDifficulty(d)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                difficulty === d
                  ? 'bg-surface-container-high text-on-surface'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {d || 'All'}
            </button>
          ))}
        </div>

        {/* Topic pills */}
        <div className="flex flex-wrap gap-1.5">
          {TOPICS.map((t) => (
            <button
              key={t}
              onClick={() => handleTopic(t)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors border ${
                topic === t
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline-variant text-on-surface-variant hover:border-primary/50 hover:text-on-surface'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-container-low rounded-xl overflow-hidden overflow-x-auto">
        <div className="min-w-[560px]">
        {/* Table header */}
        <div className="grid grid-cols-[2rem_1fr_7rem_auto_6rem] gap-4 px-5 py-3 text-[10px] uppercase tracking-widest text-on-surface-variant font-bold border-b border-outline-variant/10">
          <span>#</span>
          <span>Title</span>
          <span>Difficulty</span>
          <span>Topics</span>
          <span className="text-right">Attempts</span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : data?.problems.length === 0 ? (
          <div className="text-center py-16 text-on-surface-variant text-sm">
            No problems match the current filters.
          </div>
        ) : (
          <div className="divide-y divide-outline-variant/10">
            {data?.problems.map((problem) => (
              <button
                key={problem.id}
                onClick={() => navigate(`/training/algorithms/${problem.slug}`)}
                className="w-full grid grid-cols-[2rem_1fr_7rem_auto_6rem] gap-4 px-5 py-4 items-center hover:bg-surface-container transition-colors text-left group"
              >
                <StatusIcon status={problem.status} />

                <div>
                  <span className="text-sm font-medium text-on-surface group-hover:text-primary transition-colors">
                    {problem.order}. {problem.title}
                  </span>
                </div>

                <DifficultyBadge difficulty={problem.difficulty} />

                <div className="flex flex-wrap gap-1">
                  {problem.topics.slice(0, 2).map((t) => (
                    <span
                      key={t}
                      className="text-[10px] px-2 py-0.5 rounded bg-surface-container-high text-on-surface-variant"
                    >
                      {t}
                    </span>
                  ))}
                  {problem.topics.length > 2 && (
                    <span className="text-[10px] text-on-surface-variant">
                      +{problem.topics.length - 2}
                    </span>
                  )}
                </div>

                <span className="text-xs text-on-surface-variant text-right">
                  {problem.attempts > 0 ? problem.attempts : '—'}
                </span>
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded bg-surface-container text-on-surface-variant text-sm disabled:opacity-40 hover:bg-surface-container-high transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-on-surface-variant">
            {page} / {data.pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
            disabled={page === data.pages}
            className="px-3 py-1.5 rounded bg-surface-container text-on-surface-variant text-sm disabled:opacity-40 hover:bg-surface-container-high transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
